import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Stack,
  Switch,
  IconButton,
  Grid,
  Paper,
  Chip,
  Avatar,
  Tooltip,
  Checkbox,
  FormControlLabel,
  TextField,
} from '@mui/material';
import {
  ChevronUp,
  ChevronDown,
  GripVertical,
  Info,
} from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';

// Brand logos helper for telecom networks and billing providers
export const getCategoryLogo = (categoryName = '', service = '') => {
  const upper = `${categoryName || ''} ${service || ''}`.toUpperCase();
  if (upper.includes('MTN')) return 'https://cdn.alissoft.com.ng/apps-images/mtn.jpg';
  if (upper.includes('AIRTEL')) return 'https://cdn.alissoft.com.ng/apps-images/airtel.png';
  if (upper.includes('GLO')) return 'https://cdn.alissoft.com.ng/apps-images/glo.png';
  if (upper.includes('9MOBILE') || upper.includes('ETISALAT')) return 'https://cdn.alissoft.com.ng/apps-images/9mobile.png';
  if (upper.includes('DSTV')) return 'https://cdn.alissoft.com.ng/apps-images/dstv.png';
  if (upper.includes('GOTV')) return 'https://cdn.alissoft.com.ng/apps-images/gotv.png';
  if (upper.includes('STARTIMES')) return 'https://cdn.alissoft.com.ng/apps-images/startimes.png';
  if (upper.includes('WAEC')) return 'https://cdn.alissoft.com.ng/apps-images/waec.png';
  if (upper.includes('NECO')) return 'https://cdn.alissoft.com.ng/apps-images/neco.png';
  return null;
};

// Service color helper
export const getServiceColor = (service = '') => {
  switch (service?.toLowerCase()) {
    case 'data': return '#0284c7';
    case 'airtime': return '#16a34a';
    case 'cable': return '#9333ea';
    case 'electricity': return '#ea580c';
    case 'resultcheck': return '#ca8a04';
    default: return '#64748b';
  }
};

export default function CategoryControl({
  id,
  name,
  service,
  status,
  indexValue,
  productCount,
  onToggleStatus,
  onMoveUp,
  onMoveDown,
  showUpButton,
  showDownButton,
  successRate,
  successRateAuto,
  changeAppCategoryAvailability,
}) {
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [categoryStatus, setCategoryStatus] = useState(status);
  const [rate, setRate] = useState(successRate ?? 100);
  const [rateAuto, setRateAuto] = useState(successRateAuto ?? true);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  useEffect(() => {
    setCategoryStatus(status);
  }, [status]);

  useEffect(() => {
    setRate(successRate ?? 100);
    setRateAuto(successRateAuto ?? true);
  }, [successRate, successRateAuto]);

  const logoUrl = getCategoryLogo(name, service);
  const serviceColor = getServiceColor(service);

  const handleStatusChange = async (e) => {
    const newStatus = e.target.checked;
    setCategoryStatus(newStatus);
    if (onToggleStatus) {
      await onToggleStatus(id, newStatus);
    } else if (changeAppCategoryAvailability) {
      await changeAppCategoryAvailability(id, { status: newStatus });
    }
  };

  const handleSaveDiagnostics = async (newRate, newRateAuto) => {
    const payload = {
      status: categoryStatus,
      successRate: newRate !== undefined ? newRate : rate,
      successRateAuto: newRateAuto !== undefined ? newRateAuto : rateAuto,
      index: indexValue,
    };
    if (changeAppCategoryAvailability) {
      await changeAppCategoryAvailability(id, payload);
    }
  };

  const getRateColor = (val) => {
    if (val >= 70) return '#10b981';
    if (val >= 50) return '#f59e0b';
    return '#ef4444';
  };
  const rateColor = getRateColor(rate);

  return (
    <Paper
      ref={setNodeRef}
      style={{
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        transition,
        zIndex: isDragging ? 999 : 'auto',
      }}
      elevation={isDragging ? 4 : 0}
      sx={{
        p: { xs: 1.5, sm: 2 },
        border: '1px solid',
        borderColor: categoryStatus ? 'grey.200' : 'error.light',
        borderRadius: 2.5,
        bgcolor: isDragging ? 'grey.50' : 'white',
        opacity: categoryStatus ? 1 : 0.88,
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: categoryStatus ? 'primary.light' : 'error.main',
          boxShadow: '0 2px 8px -2px rgba(0,0,0,0.08)',
        },
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
      >
        {/* Left: Drag Handle, Position Badge, Logo & Title */}
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          <IconButton
            size="small"
            sx={{ cursor: 'grab', color: 'grey.400', p: 0.5, '&:hover': { color: 'text.primary' } }}
            {...attributes}
            {...listeners}
          >
            <GripVertical size={18} />
          </IconButton>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'primary.50',
              color: 'primary.main',
              border: '1px solid',
              borderColor: 'primary.200',
              borderRadius: '8px',
              minWidth: 28,
              height: 28,
              fontSize: '0.75rem',
              fontWeight: 800,
            }}
          >
            #{(indexValue ?? 0) + 1}
          </Box>

          {/* Logo / Avatar */}
          <Avatar
            src={logoUrl || undefined}
            sx={{
              width: 38,
              height: 38,
              bgcolor: `${serviceColor}15`,
              color: serviceColor,
              fontWeight: 700,
              fontSize: '0.85rem',
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            {name ? name.substring(0, 2).toUpperCase() : '??'}
          </Avatar>

          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="subtitle1" fontWeight={700} color="text.primary" noWrap>
                {name}
              </Typography>
              {service && (
                <Chip
                  label={service}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    bgcolor: `${serviceColor}15`,
                    color: serviceColor,
                  }}
                />
              )}
              {productCount !== undefined && (
                <Chip
                  label={`${productCount} product${productCount === 1 ? '' : 's'}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    borderColor: 'grey.300',
                    color: 'text.secondary',
                  }}
                />
              )}
            </Stack>

            <Typography variant="caption" color="text.secondary">
              Display priority: Position #{(indexValue ?? 0) + 1} on user apps
            </Typography>
          </Box>
        </Stack>

        {/* Right: Direct Kill-Switch Toggle & Ordering Chevrons */}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'space-between', sm: 'flex-end' } }}
        >
          {/* Status Chip & Toggle */}
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={categoryStatus ? 'Checkout Active' : 'Checkout Disabled'}
              size="small"
              variant={categoryStatus ? 'filled' : 'outlined'}
              color={categoryStatus ? 'success' : 'error'}
              sx={{
                fontWeight: 600,
                fontSize: '0.75rem',
                height: 24,
                bgcolor: categoryStatus ? 'success.50' : 'error.50',
                color: categoryStatus ? 'success.dark' : 'error.dark',
                borderColor: categoryStatus ? 'success.light' : 'error.light',
              }}
            />
            <Switch
              checked={Boolean(categoryStatus)}
              onChange={handleStatusChange}
              color="success"
              size="small"
            />
          </Stack>

          {/* Up & Down Chevrons */}
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ bgcolor: 'grey.100', p: 0.5, borderRadius: 1.5 }}>
            <Tooltip title="Move Up">
              <span>
                <IconButton
                  size="small"
                  onClick={onMoveUp}
                  disabled={!showUpButton}
                  sx={{ p: 0.5, '&:hover': { bgcolor: 'white' } }}
                >
                  <ChevronUp size={16} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Move Down">
              <span>
                <IconButton
                  size="small"
                  onClick={onMoveDown}
                  disabled={!showDownButton}
                  sx={{ p: 0.5, '&:hover': { bgcolor: 'white' } }}
                >
                  <ChevronDown size={16} />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          {/* Diagnostics toggle */}
          <Tooltip title="Success Rate Stats">
            <IconButton
              size="small"
              onClick={() => setShowDiagnostics(!showDiagnostics)}
              sx={{ color: showDiagnostics ? 'primary.main' : 'grey.400' }}
            >
              <Info size={16} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Optional Diagnostics dropdown */}
      {showDiagnostics && (
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed', borderColor: 'grey.200' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rateAuto}
                    onChange={(e) => {
                      const next = e.target.checked;
                      setRateAuto(next);
                      handleSaveDiagnostics(rate, next);
                    }}
                  />
                }
                label={<Typography variant="body2">Auto Calculate Success Rate</Typography>}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Manual Success Rate (%)"
                type="number"
                value={rate}
                disabled={rateAuto}
                onChange={(e) => setRate(e.target.value)}
                onBlur={() => handleSaveDiagnostics(Number(rate), rateAuto)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Current Success Rate: {rate}%
                </Typography>
                <Box sx={{ width: '100%', height: 6, bgcolor: 'grey.200', borderRadius: 3, overflow: 'hidden' }}>
                  <Box sx={{ width: `${rate}%`, height: '100%', bgcolor: rateColor }} />
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      )}
    </Paper>
  );
}

CategoryControl.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  service: PropTypes.string,
  status: PropTypes.bool,
  indexValue: PropTypes.number,
  productCount: PropTypes.number,
  onToggleStatus: PropTypes.func,
  onMoveUp: PropTypes.func,
  onMoveDown: PropTypes.func,
  showUpButton: PropTypes.bool,
  showDownButton: PropTypes.bool,
  successRate: PropTypes.number,
  successRateAuto: PropTypes.bool,
  changeAppCategoryAvailability: PropTypes.func,
};
